import { useEffect, useState } from 'react';
import { supabase } from '../supabase.js';
import {
  bidStatusLabel, count, formatDay, formatMoney, formatPounds, jobKindLabel, loadTrade, memberRoleLabel, orDash, planLabel, planTone, workStatusLabel,
} from './adminData.js';
import { SortableTable } from './SortableTable.jsx';
import { labelOf } from './sorting.js';
import { formatWhen } from './staffAdmin.js';

const checkResults = {
  verified: 'Matches Companies House',
  name_mismatch: "Name doesn't match",
  not_active: 'Not active at Companies House',
  not_found: 'Not found',
  duplicate: 'Already used by another company',
  error: 'Check failed',
  staff_verified: 'Verified by staff',
  staff_rejected: 'Rejected by staff',
};

const paymentWords = {
  unpaid: 'Not paid',
  pending: 'Payment pending',
  paid: 'Paid',
  partially_refunded: 'Part refunded',
  refunded: 'Refunded',
  disputed: 'Disputed',
};

const providerWords = { apple: 'Apple', stripe: 'Stripe' };

const when = (value) => (value ? formatWhen(value) : '–');
const yesNo = (value) => (value ? 'Yes' : 'No');
const list = (values) => (Array.isArray(values) && values.length ? values.join(', ') : '–');
const paymentLabel = (status) => (!status ? '–' : Object.hasOwn(paymentWords, status) ? paymentWords[status] : String(status));
const providerLabel = (provider) => (!provider ? '–' : Object.hasOwn(providerWords, provider) ? providerWords[provider] : String(provider));

function None() {
  return <p className="mod-note">None.</p>;
}

const memberColumns = (onOpen) => [
  {
    key: 'name',
    label: 'Name',
    value: (member) => member.name || member.email,
    cell: (member) => (
      <button type="button" className="admin-link" onClick={() => onOpen({ tab: 'people', person: member.user_id })}>
        {member.name || member.email || 'No name given'}
      </button>
    ),
  },
  { key: 'email', label: 'Email', value: (member) => member.email, cell: (member) => orDash(member.email) },
  { key: 'role', label: 'Role', value: (member) => labelOf(member.role, memberRoleLabel), cell: (member) => memberRoleLabel(member.role) },
  { key: 'active', label: 'Active', value: (member) => Boolean(member.active), first: 'desc', cell: (member) => yesNo(member.active) },
  { key: 'joined', label: 'Joined', value: (member) => member.joined_at, first: 'desc', cell: (member) => formatDay(member.joined_at) },
];

const bidColumns = [
  { key: 'job', label: 'Job', value: (bid) => bid.job_title, cell: (bid) => orDash(bid.job_title) },
  { key: 'kind', label: 'Kind', value: (bid) => labelOf(bid.kind, jobKindLabel), cell: (bid) => jobKindLabel(bid.kind) },
  { key: 'price', label: 'Price', value: (bid) => bid.price, first: 'desc', cell: (bid) => formatPounds(bid.price) },
  { key: 'status', label: 'Status', value: (bid) => labelOf(bid.status, bidStatusLabel), cell: (bid) => bidStatusLabel(bid.status) },
  { key: 'sent', label: 'Sent', value: (bid) => bid.sent_at, first: 'desc', cell: (bid) => when(bid.sent_at) },
];

const workColumns = [
  { key: 'job', label: 'Job', value: (item) => item.title, cell: (item) => orDash(item.title) },
  { key: 'status', label: 'Status', value: (item) => labelOf(item.status, workStatusLabel), cell: (item) => workStatusLabel(item.status) },
  { key: 'value', label: 'Value', value: (item) => item.amount_minor, first: 'desc', cell: (item) => formatMoney(item.amount_minor, item.currency || 'GBP') },
  { key: 'payment', label: 'Payment', value: (item) => labelOf(item.payment_status, paymentLabel), cell: (item) => paymentLabel(item.payment_status) },
  { key: 'started', label: 'Started', value: (item) => item.created_at, first: 'desc', cell: (item) => formatDay(item.created_at) },
  { key: 'completed', label: 'Completed', value: (item) => item.completed_at, first: 'desc', cell: (item) => formatDay(item.completed_at) },
];

const reviewColumns = [
  { key: 'rating', label: 'Rating', value: (review) => review.rating, first: 'desc', cell: (review) => `${orDash(review.rating)} out of 5` },
  { key: 'left', label: 'Left', value: (review) => review.created_at, first: 'desc', cell: (review) => formatDay(review.created_at) },
];

const viewColumns = [
  { key: 'by', label: 'Staff member', value: (view) => view.by, cell: (view) => `${orDash(view.by)}${view.sensitive ? ' · with private details' : ''}` },
  { key: 'when', label: 'When', value: (view) => view.viewed_at, first: 'desc', cell: (view) => when(view.viewed_at) },
];

function averageText(value) {
  const number = value === null || value === undefined || value === '' ? NaN : Number(value);
  return Number.isFinite(number) ? number.toFixed(1) : '–';
}

function checkText(check) {
  if (!check) return 'Not checked yet';
  const result = Object.hasOwn(checkResults, check.result) ? checkResults[check.result] : orDash(check.result);
  const found = check.result === 'name_mismatch' && check.found_name ? ` (Companies House has ${check.found_name})` : '';
  return [
    `${result}${found}`,
    check.company_status && `Companies House status: ${String(check.company_status).replace(/_/g, ' ')}`,
    check.checked_at && `checked ${formatDay(check.checked_at)}`,
  ].filter(Boolean).join(' · ');
}

/** Everything we hold on one trade company. Opening it is logged by the database. */
export default function TradeRecord({ id, onOpen }) {
  const [state, setState] = useState({ record: null, error: '' });

  useEffect(() => {
    let live = true;
    setState({ record: null, error: '' });
    (async () => {
      try {
        const record = await loadTrade(supabase, id);
        if (live) setState({ record, error: '' });
      } catch (cause) {
        if (live) setState({ record: null, error: cause.message });
      }
    })();
    return () => { live = false; };
  }, [id]);

  const back = () => onOpen({ tab: 'trades' });
  const { record, error } = state;

  if (error) {
    return (
      <>
        <div className="admin-record-head">
          <div className="admin-head-actions"><button type="button" className="button button-secondary" onClick={back}>← Back to trades</button></div>
        </div>
        <p className="mod-error" role="alert">{error}</p>
      </>
    );
  }
  if (!record) return <p>Loading this trade company…</p>;

  const company = record.company || {};
  const members = record.members || [];
  const plan = record.plan || {};
  const bids = record.bids || [];
  const work = record.work || [];
  const reviews = record.reviews || {};
  const latestReviews = reviews.latest || [];
  const totals = record.totals || {};
  const views = record.views || [];
  const verified = !!company.verified_at;
  const reviewCount = Number(reviews.count) || 0;
  const accreditations = (company.accreditations || [])
    .map((item) => (item.reference ? `${item.name} (${item.reference})` : item.name))
    .filter(Boolean);

  return (
    <>
      <div className="admin-record-head">
        <div>
          <h2>{company.name || 'Trade company'}</h2>
          <span className={`admin-status ${verified ? 'is-good' : 'is-neutral'}`}>{verified ? 'Verified' : 'Not verified'}</span>
        </div>
        <div className="admin-head-actions">
          <button type="button" className="button button-secondary" onClick={back}>← Back to trades</button>
        </div>
      </div>

      <h2 className="admin-section">Company</h2>
      <dl className="mod-fields">
        <dt>Company number</dt><dd>{orDash(company.registration_number)}</dd>
        <dt>Joined</dt><dd>{formatDay(company.created_at)}</dd>
        <dt>Trades</dt><dd>{list(company.trades)}</dd>
        <dt>Areas covered</dt><dd>{list(company.service_areas)}</dd>
        <dt>Base area</dt><dd>{orDash(company.base)}</dd>
        <dt>Accreditations</dt><dd>{list(accreditations)}</dd>
        <dt>Companies House check</dt><dd>{checkText(company.latest_check)}</dd>
        <dt>Card payments set up</dt><dd>{yesNo(company.payments_set_up)}</dd>
        <dt>Suspended</dt><dd>{yesNo(company.suspended)}</dd>
        <dt>About</dt><dd>{orDash(company.description)}</dd>
      </dl>

      <h2 className="admin-section">People</h2>
      {members.length === 0 ? <None/> : <SortableTable columns={memberColumns(onOpen)} rows={members} rowKey={(member) => member.user_id}/>}

      <h2 className="admin-section">Plan and bids</h2>
      <dl className="mod-fields">
        <dt>Plan</dt><dd><span className={`admin-status is-${planTone(plan.status)}`}>{planLabel(plan.status)}</span></dd>
        <dt>Paid through</dt><dd>{formatDay(plan.status === 'lapsed' ? plan.last_valid_until : plan.valid_until)}</dd>
        <dt>Pays through</dt><dd>{providerLabel(plan.provider)}</dd>
        <dt>Bids this month</dt><dd>{count(plan.bids_this_month)}</dd>
        <dt>Free bids left this month</dt><dd>{plan.free_bids_left === null || plan.free_bids_left === undefined ? '–' : count(plan.free_bids_left)}</dd>
      </dl>
      {plan.limit_on === false && <p className="mod-note">Bid limits are switched off, so they can keep bidding.</p>}

      <h2 className="admin-section">Totals</h2>
      <div className="mod-summary" aria-label="Totals for this trade company">
        <div><strong>{count(totals.bids)}</strong><span>Bids</span></div>
        <div><strong>{count(totals.won)}</strong><span>Jobs won</span></div>
        <div><strong>{count(totals.completed)}</strong><span>Completed</span></div>
        <div><strong>{formatMoney(totals.completed_value_minor)}</strong><span>Value of completed work</span></div>
      </div>

      <h2 className="admin-section">Bids</h2>
      {bids.length === 0 ? <None/> : <SortableTable columns={bidColumns} rows={bids} rowKey={(bid) => `${bid.kind}-${bid.job_id}-${bid.sent_at}`}/>}

      <h2 className="admin-section">Work</h2>
      {work.length === 0 ? <None/> : <SortableTable columns={workColumns} rows={work} rowKey={(item) => item.id}/>}

      <h2 className="admin-section">Reviews</h2>
      <p>{reviewCount === 0 ? 'No reviews yet' : `${reviewCount} ${reviewCount === 1 ? 'review' : 'reviews'}, average ${averageText(reviews.average)}`}</p>
      {latestReviews.length > 0 && <SortableTable columns={reviewColumns} rows={latestReviews} rowKey={(review, index) => `${review.created_at}-${index}`}/>}

      <h2 className="admin-section">Who has viewed this record</h2>
      {views.length === 0 ? <None/> : <SortableTable columns={viewColumns} rows={views} rowKey={(view, index) => `${view.viewed_at}-${index}`}/>}
    </>
  );
}
